<template>
  <AppModal
    :title="labels.title"
    content-class="smtp-dialog column no-wrap"
    modal-body-class="col scroll"
    position="right"
    is-open
    no-maximized
    show-close-btn
    @close="$emit('close')"
  >
    <div class="q-px-xl q-py-lg">
      <div class="q-pb-lg q-mb-lg border-bottom">
        <AppInput
          v-model="model.title"
          :label="$t('label.smtpName')"
          :placeholder="$t('placeholder.title')"
        />
        <AppInput
          v-model="model.from.name"
          :label="$t('label.fromName')"
          :placeholder="$t('placeholder.name')"
        />
        <AppInput
          v-model="model.from.email"
          :label="$t('label.fromEmail')"
          placeholder="sale@mydomain.com"
        />
        <AppInput
          v-model="model.server.address"
          :label="$t('label.smtpServer')"
          placeholder="smtp.domain.com"
        />
        <div class="row">
          <div class="col q-mr-md">
            <div
              v-t="'label.smtpServer'"
              class="label flex items-center"
            />
            <AppButtonToggle
              v-model="model.server.encryption"
              :options="smtpServerOptions"
              class="toggle-buttons"
              toggle-color="btn-toggle-selected"
              toggle-text-color="black"
              text-color="black"
              padding="13px 22px"
              spread
            />
          </div>
          <AppInput
            v-model="model.server.port"
            :label="$t('label.port')"
            class="col-3"
            placeholder="465"
          />
        </div>
      </div>
      <div :class="{ 'q-pb-lg q-mb-lg border-bottom': model.authentication.enable }">
        <AppToggle
          v-model="model.authentication.enable"
          :label="$t('label.smtpAuth')"
          class="justify-between full-width"
          left-label
        />
      </div>
      <div
        v-if="model.authentication.enable"
        class="row q-col-gutter-x-md"
      >
        <AppInput
          v-model="model.authentication.account"
          :label="$t('label.smtpUser')"
          class="col"
        />
        <AppPassword
          v-model="model.authentication.password"
          :label="$t('label.smtpPassword')"
          class="col"
        />
      </div>
    </div>
    <template #footer>
      <div class="border-top q-pa-lg flex full-width">
        <AppButton
          :label="labels.cancel"
          :icon-left="isCreate ? undefined : 'fl:trash'"
          icon-left-color="grey-7"
          icon-left-size="24px"
          class="q-mr-lg"
          color="white"
          text-color="black"
          outline
          @click="handlers.cancel"
        />
        <AppButton
          :label="labels.add"
          :loading="loading"
          class="col text-21-700"
          text-color="black"
          padding="13px 22px"
          @click="onSave"
        />
      </div>
    </template>
  </AppModal>
</template>

<script>
import AppInput from 'common/components/AppInput';
import AppModal from 'common/components/AppModal';
import AppPassword from 'common/components/AppPassword';
import AppToggle from 'common/components/AppToggle';
import AppButton from 'common/components/buttons/AppButton';
import AppButtonToggle from 'common/components/buttons/AppButtonToggle';
import handleError from 'common/helpers/handleError';
import { cloneObject } from 'common/helpers/objectHelper';
import { mapActions } from 'vuex';

const defaultSmtp = () => ({
  title: undefined,
  guid: undefined,
  from: {
    name: undefined,
    email: undefined,
  },
  server: {
    address: undefined,
    encryption: undefined,
    port: undefined,
  },
  authentication: {
    enable: false,
    account: undefined,
    password: undefined,
  },
});
export default {
  name: 'SmtpFormDialog',
  components: {
    AppButton,
    AppButtonToggle,
    AppInput,
    AppModal,
    AppPassword,
    AppToggle,
  },
  props: {
    value: {
      type: Object,
      default: undefined,
    },
    isCreate: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'remove'],
  data() {
    return {
      loading: false,
      model: undefined,
    };
  },
  computed: {
    labels() {
      const map = {
        create: {
          title: this.$t('title.addSmtp'),
          cancel: this.$t('button.cancel'),
          add: this.$t('button.addSmtp'),
        },
        update: {
          title: this.$t('title.editSmtp'),
          cancel: this.$t('button.delete'),
          add: this.$t('button.save'),
        },
      };

      return this.isCreate ? map.create : map.update;
    },
    handlers() {
      const map = {
        create: {
          cancel: this.onCancel,
        },
        update: {
          cancel: this.onDelete,
        },
      };
      return this.isCreate ? map.create : map.update;
    },
    smtpServerOptions() {
      return [
        {
          value: undefined,
          label: this.$t('label.none'),
        },
        {
          value: 'ssl',
          label: this.$t('label.ssl'),
        },
        {
          value: 'tls',
          label: this.$t('label.tls'),
        },
      ];
    },
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler() {
        this.model = this.value ? cloneObject(this.value) : defaultSmtp();
      },
    },
  },
  methods: {
    ...mapActions('smtp', ['createSmtp', 'updateSmtp']),
    onCancel() {
      this.$emit('close');
    },
    onDelete() {
      this.$emit('remove');
    },
    async onSave() {
      try {
        this.loading = true;
        const method = this.isCreate ? this.createSmtp : this.updateSmtp;
        await method(this.model);
        this.$emit('close');
      } catch (error) {
        handleError(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.label {
  height: 50px;
}
</style>

<style lang="scss">
.smtp-dialog {
  width: 527px;
  max-height: calc(100vh - 96px);
}
</style>
